import { useContext } from "react";
import { FieldValues } from "react-hook-form";
import { FormContext } from "./Form.context";
import { FormContextProps } from "./Form.props";
import { useWatch } from "./Form.utils";

/**
 * @deprecated
 */
export function useFormContext<T extends FieldValues = FieldValues>(): FormContextProps<T> {
  return useContext(FormContext);
}

/**
 * @deprecated
 */
interface UseFormFieldProps {
  disabled?: boolean;
}

/**
 * @deprecated
 */
export function useFormField(name: string, props?: UseFormFieldProps): ReturnType<typeof useWatch> {
  const { control } = useFormContext();
  return useWatch({ ...props, name, control });
}

/**
 *
 * useFormFields
 *  -- like useWatch, but gives data in object instead of list.
 *  -- doesn't nest
 *
 * CAUTION: This hook creates a new object on every render.
 *
 * Take care when using the output as a dependency of another hook.
 * ...to avoid accidentally causing problems, recommended usage is to destructure
 * immediately.
 *
 * Recommended approach to get proper typing:
 *
 *   // types file
 *   type MyFormData = {field1: string, field2: number, ... }; // typical form data type
 *
 *   // useMyFormFields.ts
 *   import { MyFormData } from "@/types";
 *
 *   export function useMyFormFields<Field extends keyof MyFormData=keyof MyFormData>(fields?: readonly Field[]): Pick<MyFormData, Field>  {
 *     return useFormFields(fields);
 *   }
 *
 *   // meanwhile, somewhere in form ...
 *   const { field1, field2 } = useMyFormFields(["field1", "field2"]); // field1: string, field2: number, yay!
 */

/**
 * @deprecated
 */
export function useFormFields<FormData, Field extends keyof FormData>(names?: ReadonlyArray<Field>): Pick<FormData, Field> {
  const { control } = useFormContext();
  // @ts-ignore
  const values = useWatch<FormData>({ name: names, control });
  if (names == null) {
    /* if names are not specified, useWatch returns entire formdata object
       @ts-ignore */
    return values;
  }

  const obj = {} as FormData;
  for (let i = 0; i < names.length; i++) {
    // @ts-ignore
    obj[names[i]] = values[i];
  }
  return obj;
}
